import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import AdminLayout from '../components/AdminLayout';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Sale {
  id: string;
  customer_name: string;
  customer_type: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  sale_date: string;
  created_at: string;
}

interface Expense {
  id: string;
  category: string;
  amount: number;
  expense_date: string;
}

interface MonthlyProfit {
  month: string;
  sales: number;
  expenses: number;
  profit: number;
}

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function ProfitsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState<Sale[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [monthlyProfits, setMonthlyProfits] = useState<MonthlyProfit[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [chartData, setChartData] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [expensesByCategory, setExpensesByCategory] = useState<any[]>([]);

  useEffect(() => {
    checkUser();
    loadData();
  }, []);

  useEffect(() => {
    calculateProfits();
  }, [sales, expenses, selectedPeriod]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    } else {
      setUser(user);
    }
    setLoading(false);
  };

  const loadData = async () => {
    try {
      const [salesRes, expensesRes] = await Promise.all([
        supabase.from('sales').select('*').order('sale_date', { ascending: false }),
        supabase.from('expenses').select('*').order('expense_date', { ascending: false })
      ]);

      if (salesRes.data) setSales(salesRes.data);
      if (expensesRes.data) setExpenses(expensesRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  const calculateProfits = () => {
    const monthsMap = new Map<string, { sales: number; expenses: number }>();

    // Filtrar por período
    const now = new Date();
    const filterDate = (dateStr: string) => {
      const date = new Date(dateStr);
      if (selectedPeriod === 'month') {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      } else if (selectedPeriod === 'quarter') {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const dateQuarter = Math.floor(date.getMonth() / 3);
        return dateQuarter === currentQuarter && date.getFullYear() === now.getFullYear();
      } else if (selectedPeriod === 'semester') {
        const currentSemester = Math.floor(now.getMonth() / 6);
        const dateSemester = Math.floor(date.getMonth() / 6);
        return dateSemester === currentSemester && date.getFullYear() === now.getFullYear();
      } else if (selectedPeriod === 'year') {
        return date.getFullYear() === now.getFullYear();
      }
      return true;
    };

    // Agrupar ventas por mes
    sales.filter(s => filterDate(s.sale_date || s.created_at)).forEach(sale => {
      const date = new Date(sale.sale_date || sale.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const current = monthsMap.get(monthKey) || { sales: 0, expenses: 0 };
      current.sales += sale.total_amount;
      monthsMap.set(monthKey, current);
    });

    // Agrupar gastos por mes
    expenses.filter(e => filterDate(e.expense_date)).forEach(expense => {
      const date = new Date(expense.expense_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const current = monthsMap.get(monthKey) || { sales: 0, expenses: 0 };
      current.expenses += expense.amount;
      monthsMap.set(monthKey, current);
    });

    // Convertir a array y calcular utilidades
    const profits: MonthlyProfit[] = Array.from(monthsMap.entries())
      .map(([month, data]) => ({
        month,
        sales: data.sales,
        expenses: data.expenses,
        profit: data.sales - data.expenses
      }))
      .sort((a, b) => b.month.localeCompare(a.month));

    setMonthlyProfits(profits);

    // Preparar datos para gráficos
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      return date;
    });

    const chartDataTemp = last30Days.map(date => {
      const dateStr = date.toISOString().split('T')[0];
      const daySales = sales.filter(sale => 
        (sale.sale_date || sale.created_at).startsWith(dateStr)
      ).reduce((sum, sale) => sum + parseFloat(String(sale.total_amount)), 0) || 0;

      const dayExpenses = expenses.filter(expense => 
        expense.expense_date.startsWith(dateStr)
      ).reduce((sum, expense) => sum + parseFloat(String(expense.amount)), 0) || 0;

      return {
        date: date.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' }),
        ventas: daySales,
        gastos: dayExpenses,
        ganancia: daySales - dayExpenses
      };
    });

    setChartData(chartDataTemp);

    // Datos mensuales para gráfico de barras
    const monthlyDataTemp = profits.slice(0, 6).reverse().map(p => ({
      month: getMonthName(p.month).split(' ')[0],
      ventas: p.sales,
      gastos: p.expenses,
      ganancia: p.profit
    }));

    setMonthlyData(monthlyDataTemp);

    // Gastos por categoría
    const categoryMap = new Map<string, number>();
    expenses.forEach(expense => {
      const category = expense.category || 'Sin categoría';
      categoryMap.set(category, (categoryMap.get(category) || 0) + parseFloat(String(expense.amount)));
    });

    const expensesByCategoryTemp = Array.from(categoryMap.entries()).map(([name, value]) => ({
      name,
      value
    }));

    setExpensesByCategory(expensesByCategoryTemp);
  };

  const getTotalSales = () => {
    return monthlyProfits.reduce((sum, m) => sum + m.sales, 0);
  };

  const getTotalExpenses = () => {
    return monthlyProfits.reduce((sum, m) => sum + m.expenses, 0);
  };

  const getTotalProfit = () => {
    return getTotalSales() - getTotalExpenses();
  };

  const getMonthName = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const getPeriodLabel = () => {
    if (selectedPeriod === 'all') return 'total';
    if (selectedPeriod === 'year') return 'del año';
    if (selectedPeriod === 'quarter') return 'del trimestre';
    if (selectedPeriod === 'semester') return 'del semestre';
    return 'del mes';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Calcular estadísticas del mes actual
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const monthSales = sales.filter(sale => {
    const saleDate = new Date(sale.sale_date || sale.created_at);
    return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
  }).reduce((sum, sale) => sum + parseFloat(String(sale.total_amount)), 0) || 0;

  const monthExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.expense_date);
    return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear;
  }).reduce((sum, expense) => sum + parseFloat(String(expense.amount)), 0) || 0;

  const monthProfit = monthSales - monthExpenses;
  const profitMargin = monthSales > 0 ? ((monthProfit / monthSales) * 100).toFixed(1) : '0.0';

  const totalSales = getTotalSales();
  const totalExpenses = getTotalExpenses();
  const totalProfit = getTotalProfit();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout currentPage="profits">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Análisis de Ganancias</h2>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">Visualiza el rendimiento financiero del negocio</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-xl sm:text-2xl text-green-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Ganancia del Mes</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(monthProfit)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-line-chart-line text-xl sm:text-2xl text-blue-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Ventas del Mes</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(monthSales)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-shopping-cart-line text-xl sm:text-2xl text-red-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Gastos del Mes</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(monthExpenses)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-percent-line text-xl sm:text-2xl text-purple-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Margen de Ganancia</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{profitMargin}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Tendencia de Ganancias (Últimos 30 días)</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="ventas" stroke="#10b981" strokeWidth={2} name="Ventas" />
                <Line type="monotone" dataKey="gastos" stroke="#ef4444" strokeWidth={2} name="Gastos" />
                <Line type="monotone" dataKey="ganancia" stroke="#3b82f6" strokeWidth={2} name="Ganancia" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Comparación Mensual</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="ventas" fill="#10b981" name="Ventas" />
                <Bar dataKey="gastos" fill="#ef4444" name="Gastos" />
                <Bar dataKey="ganancia" fill="#3b82f6" name="Ganancia" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Resumen Financiero</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-up-line text-green-600 text-base sm:text-lg"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Total Ingresos</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 truncate">{formatCurrency(totalSales)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-down-line text-red-600 text-base sm:text-lg"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Total Egresos</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 truncate">{formatCurrency(totalExpenses)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-wallet-line text-blue-600 text-base sm:text-lg"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600">Ganancia Neta</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 truncate">{formatCurrency(totalProfit)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Distribución de Gastos</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expensesByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={window.innerWidth < 640 ? 60 : 80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
